import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { fetchRtDeviceSettings, upsertRtDeviceSetting } from '~/api/rt-device-settings';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import { RtDeviceSetting, RtDeviceSettingPayload, RtDeviceSettingQueryKeys } from '~/types/rt-device-settings';

const useAllRtDeviceSettings = () => {
  const companyId = useActiveCompanyId();
  return useQuery({
    queryKey: RtDeviceSettingQueryKeys.company(companyId),
    queryFn: () => fetchRtDeviceSettings(companyId),
    enabled: !!companyId,
  });
};

export const useUpsertRtDeviceSetting = () => {
  const companyId = useActiveCompanyId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ counterpartId, payload }: { counterpartId: string; payload: RtDeviceSettingPayload }) =>
      upsertRtDeviceSetting(counterpartId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RtDeviceSettingQueryKeys.company(companyId) });
    },
  });
};

export const useRtDeviceSettingsMap = () => {
  const { data: deviceSettings } = useAllRtDeviceSettings();

  return useMemo(() => {
    const map = new Map<string, RtDeviceSetting>();
    if (!deviceSettings) return map;

    for (const setting of deviceSettings) {
      if (setting.counterpart?.id) {
        map.set(setting.counterpart.id, setting);
      }
    }
    return map;
  }, [deviceSettings]);
};
